import {
    useMutation,
    useQueries,
    useQueryClient,
} from '@tanstack/react-query';

import {
    fetchControl,
    updateControlState,
} from '../services/PlatformControlsService';
import {
    ControlState,
    PlatformControlRecord,
} from '../types';

export const platformControlsKeys = {
    all: ['platform-controls'] as const,
    detail: (id: string) => [...platformControlsKeys.all, id] as const,
};

/**
 * Read the live position of each wired control.
 *
 * One query per control rather than a list query: each control's state lives in
 * its own place (the registration switch is an SSM parameter), so there is no
 * single list to read. `useQueries` keeps them independent — one control failing
 * to read must not blank the others.
 *
 * `staleTime` is deliberately short. These are switches an operator may have just
 * flipped from elsewhere (the CLI backstop path), so a stale cached position is
 * actively misleading.
 */
export const useWiredControlsQuery = (ids: string[]) =>
    useQueries({
                   queries: ids.map((id) => ({
                       queryKey: platformControlsKeys.detail(id),
                       queryFn: () => fetchControl(id),
                       staleTime: 10_000,
                       retry: 1,
                   })),
               });

export const useSetControlStateMutation = () => {

    const queryClient = useQueryClient();

    return useMutation<PlatformControlRecord | null, Error, { id: string; state: ControlState }>({
        mutationFn: ({
                         id,
                         state
                     }) => updateControlState(id, state),
        onSuccess: (_result, {id}) => {
            // Re-read rather than trusting the mutation's echo: the authoritative
            // position is whatever the parameter now holds, and a concurrent flip
            // from the CLI path would otherwise be papered over.
            void queryClient.invalidateQueries({queryKey: platformControlsKeys.detail(id)});
        },
    });
};
